import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Phone, LogOut, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

export default function MOTOLayout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);

  // MOTO session countdown timer (30 minutes)
  useEffect(() => {
    if (user?.role === "moto" && user?.expires_at) {
      const updateTimeLeft = () => {
        const now = new Date();
        const expiresAt = new Date(user.expires_at);
        const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
        
        if (diff === 0) {
          logout();
        } else {
          setSessionTimeLeft(diff);
        }
      };

      updateTimeLeft();
      const interval = setInterval(updateTimeLeft, 1000);
      return () => clearInterval(interval);
    } else {
      setSessionTimeLeft(null);
    }
  }, [user, logout]);

  const formatTimeLeft = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-purple-600 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden bg-white/10">
                <img src="/logo.png" alt="GeekTickets" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg">GeekTickets</h1>
                <p className="text-xs text-purple-200">MOTO Payments</p>
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-4">
              {/* Session Timer */}
              {sessionTimeLeft !== null && (
                <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-lg">
                  <Clock className="w-4 h-4" />
                  <span className={`text-sm font-mono ${sessionTimeLeft < 120 ? "text-yellow-300 font-bold" : ""}`}>
                    {formatTimeLeft(sessionTimeLeft)}
                  </span>
                </div>
              )}

              {/* Role Badge */}
              <Badge 
                variant="outline" 
                className="bg-white/10 text-white border-white/20"
              >
                <Phone className="w-3 h-3 mr-1" />
                MOTO
              </Badge>

              {/* Logout Button */}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="moto-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-muted/50 border-t border-border py-2">
        <div className="max-w-4xl mx-auto px-4 text-center text-xs text-muted-foreground">
          Session expires in {sessionTimeLeft !== null ? formatTimeLeft(sessionTimeLeft) : "30:00"} • 
          Card details are not stored
        </div>
      </footer>
    </div>
  );
}
